// Module for representing gamestate in Vuex
// The base gamestate is the state of all objects in the game at a certain snapshot

import EntityStore from './EntityStore';
import jsonpatch from 'fast-json-patch';
import _ from 'lodash';

// TODO: type this — Vuex module context and game state types
/* eslint-disable @typescript-eslint/no-explicit-any */
type Ctx = any;
type GameState = any;
/* eslint-enable @typescript-eslint/no-explicit-any */

// Invariant: gamestate = foldl(base, applyDeltas, deltas)
const GameStateStore = {
    namespaced: true,
    state: {
        base: {
            global: {} as Record<string, unknown>,
            entities: {} as Record<string, unknown>,
            ruleset: {} as Record<string, unknown>,
        },
        global: {
            rollLog: [] as unknown[],
        },
        ruleset: {} as Record<string, unknown>,
        selectedTokenId: '',
        activeMap: null as unknown | null,
        mapShapes: [] as unknown[],
    },
    modules: {
        entities: EntityStore,
    },
    getters: {
        base: (state: GameState) => {
            return state.base;
        },
        ruleset: (state: GameState) => {
            return state.ruleset;
        },
        actions: (state: GameState) => {
            return state.ruleset.actions;
        },
        rollLog: (state: GameState) => {
            return state.global.rollLog;
        },
        selectedTokenId: (state: GameState) => {
            return state.selectedTokenId;
        },
    },
    mutations: {
        selectedTokenUpdate(state: GameState, tokenId: string): void {
            state.selectedTokenId = tokenId;
        },
        setMapShapes(state: GameState, shapes: unknown[]): void {
            state.mapShapes = shapes;
        },
        clear(state: GameState): void {
            state.entities = {};
            state.global = { rollLog: [] };
        },
        setBase(state: GameState): void {
            state.base.entities = _.cloneDeep(state.entities);
            state.base.global = _.cloneDeep(state.global);
            state.base.ruleset = _.cloneDeep(state.ruleset);
        },
        activateMap(state: GameState, map: unknown): void {
            state.activeMap = map;
        },
    },
    actions: {
        clear({ commit }: Ctx): void {
            commit('clear');
        },
        setBase({ commit }: Ctx): void {
            commit('setBase');
        },
        activateMap({ commit }: Ctx, map: unknown): void {
            commit('activateMap', map);
        },
        // TODO - Delete this
        applyDelta({ dispatch }: Ctx, delta: Ctx): void {
            if (!Array.isArray(delta)) delta = [delta];

            delta.forEach((patch: Ctx) => {
                dispatch('patch', patch);
            });
        },

        // Patch is a list of JSON patch operations as described in: http://jsonpatch.com/
        // IMPORTANT POINT TO NOTE: Vue cannot detect property addition or deletion.
        // What needs to be done is check if an operation is anything else than a "replace"
        // And depending on that, handle the Vue reactivity stuff when applying the patch.
        patch({ state }: Ctx, patch: Ctx): void {
            if (!Array.isArray(patch)) patch = [patch];
            if (patch.length == 0) return;

            // Dry run the JSONpatch to allow for aborting
            // If this throws an error, the patch is not applied
            jsonpatch.applyPatch(state, patch, true, false).pop()!.newDocument;

            // Execute the patch with appropriate vue operations when needed
            patch.forEach((change: Ctx) => {
                const followPath = (path: string[]) => {
                    return path.reduce((acc: Ctx, el: string) => {
                        return acc[el];
                    }, state);
                };

                const parsePath = (pathString: string): string[] => {
                    const path = pathString.split('/');
                    path.shift(); // remove the first, empty element

                    if (path[0] != 'entities' && path[0] != 'global')
                        throw new Error('Attempted to touch protected property: ' + path[0]);

                    // Handle JSONPatch syntactic sugar
                    path.forEach((el, idx) => {
                        switch (el) {
                            case '-': {
                                const target = followPath(path.slice(0, idx));
                                if (!Array.isArray(target)) {
                                    throw new Error('Cannot use array index syntax on non-array value');
                                }
                                path[idx] = String(target.length);
                                break;
                            }
                        }
                    });
                    return path;
                };

                const path = parsePath(change.path);

                switch (change.op) {
                    case 'replace':
                        jsonpatch.applyPatch(state, [change]);
                        break;
                    case 'add': {
                        const prop = path.pop()!;
                        if (Array.isArray(followPath(path))) {
                            followPath(path).splice(prop, 0, change.value); // Insert the value at the given index
                        } else {
                            followPath(path)[prop] = change.value;
                        }
                        break;
                    }
                    case 'remove': {
                        const prop = path.pop()!;

                        if (prop in state) throw new Error('Attempted to remove protected property: ' + prop);
                        const removeTarget = followPath(path);
                        if (Array.isArray(removeTarget)) {
                            removeTarget.splice(parseInt(prop), 1);
                        } else {
                            delete removeTarget[prop];
                        }
                        break;
                    }
                    case 'move': {
                        const from = parsePath(change.from);
                        const moved = _.cloneDeep(followPath(from));

                        const fromProp = from.pop()!;

                        if (fromProp in state) throw new Error('Attempted to remove protected property: ' + fromProp);
                        delete followPath(from)[fromProp];

                        const prop = path.pop()!;
                        followPath(path)[prop] = moved;
                        break;
                    }
                    case 'copy': {
                        const from = parsePath(change.from);
                        const copied = _.cloneDeep(followPath(from));

                        const prop = path.pop()!;
                        followPath(path)[prop] = copied;
                        break;
                    }
                    case 'test':
                        // This one is already handled by the dry run. It does not change anything to the state
                        break;
                }
            });
        },
    },
};

export default GameStateStore;
